import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock';
import adjustEvents from './json/adjust-events.json';
import { getAnalyticsOptions, identify, track, trackLink } from './utils/analytics';
import { stickyHeader } from './utils/header';
import { checkFetchForError, makeSlider, parseFetchResponse } from './utils/helpers';
import { generateFbc, generateFbp } from './utils/fb-tracking';

function parseCookies() {
	return document.cookie.split(';').reduce((cookies, cookie) => {
		const [name, value] = cookie.trim().split('=');
		if (name && value) {
			cookies[name] = decodeURIComponent(value);
		}
		return cookies;
	}, {});
}

function clearCookie(name) {
	document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
}

function getAdParams() {
	const THIRTY_DAYS_IN_MS = 30 * 24 * 60 * 60 * 1000;
	let params = {};

	const cookies = parseCookies();
	const cookieTimestamp = cookies['ad_timestamp'];
	if (cookieTimestamp) {
		const age = Date.now() - parseInt(cookieTimestamp, 10);
		if (age <= THIRTY_DAYS_IN_MS) {
			params.fbclid = cookies['fbclid'];
			params.gclid = cookies['gclid'];
			params.msclkid = cookies['msclkid'];
			params.fbp = cookies['_fbp'] || generateFbp();
			params.fbc = cookies['_fbc'] || generateFbc(cookies['fbclid']);
			return params;
		} else {
			clearCookie('fbclid');
			clearCookie('gclid');
			clearCookie('msclkid');
			clearCookie('ad_timestamp');
		}
	}

	const adParams = ['fbclid', 'gclid', 'msclkid'];
	for (const adParam of adParams) {
		const data = window.localStorage.getItem(adParam);
		if (!data) return null;
		const parsedData = JSON.parse(data);
		const result = Date.now() - parsedData.timestamp <= THIRTY_DAYS_IN_MS ? parsedData.value : null;
		params[adParam] = result;
	}

	params.fbp = cookies['_fbp'] || generateFbp();
	params.fbc = cookies['_fbc'] || generateFbc(params.fbclid);

	return params;
}

(function() {
	var optInAnalytics = document.getElementById('opt-in-analytics').getAttribute('data-opt-in-analytics');

	const adParams = getAdParams();

	setTimeout(function() {
		document.body.classList.remove('preload');
	}, 100);

	window.analytics.addSourceMiddleware(({ payload, next }) => {
		payload.obj.context = {
			...payload.obj.context,
			fbp: adParams.fbp,
			fbc: adParams.fbc,
		};

		payload.obj.properties = {
			...(payload.obj.properties || {}),
			action_source: "website",
			fbp: adParams.fbp,
			fbc: adParams.fbc,
		};

		next(payload);
	});

	window.analytics.ready(function() {
		var userId = document.getElementById('user-id') ? document.getElementById('user-id').getAttribute('data-user-id') : null;
		var pageUrlBase = window.location.hostname;
		var pageUrlPath = window.location.pathname;
		var pageTrackObj = { url_base: pageUrlBase };
		var anonymousId = document.getElementById('anonymous-id') ? document.getElementById('anonymous-id').value : null;
		var urlParams = new URLSearchParams(window.location.search);

		function campaignParams() {
			const campaign_keywords = 'utm_source utm_medium utm_campaign utm_content utm_term'.split(' ');
			let kw = '';
			let params = {};
			let first_params = {};

			for (let i = 0; i < campaign_keywords.length; ++i) {
				kw = urlParams.get(campaign_keywords[i]);
				if (kw && kw.length) {
					params[campaign_keywords[i] + ' [last touch]'] = kw;
					first_params[campaign_keywords[i] + ' [first touch]'] = kw;
				}
			}

			window.mixpanel.people.set(params);
			window.mixpanel.people.set_once(first_params);
			window.mixpanel.register(params);
		}

		campaignParams();

		if (userId && optInAnalytics) identify(userId);

		if (typeof window.analytics.user === 'function') {
			if (anonymousId) {
				// If server provides the anonymousId by any reason
				window.analytics.user().anonymousId(anonymousId);
			}
			window.optimizelyAnonymousId = window.analytics.user().anonymousId();
		}

		window.analytics.page({ app: 'Growth' }, getAnalyticsOptions());

		if (document.getElementById('analytics-info')) {
			pageTrackObj = JSON.parse(document.getElementById('analytics-info').getAttribute('data-analytics-info'));
		} else if (['/', '/home'].includes(pageUrlPath)) {
			pageTrackObj.page_name = 'home page';
		} else if (pageUrlPath === '/hulu') {
			pageTrackObj.page_name = 'hulu';
		} else if (pageUrlPath === '/tv') {
			pageTrackObj.page_name = 'hulu tv';
		} else {
			pageTrackObj.page_name = pageUrlPath.split('/').pop().replace('-', ' ');
		}

		// This "Page Viewed" tracking event should only happen on select pages;
		// let's first ensure that the pageTrackObj and page_name are defined before sending it off
		if (pageTrackObj && Object.keys(pageTrackObj).length && pageTrackObj.page_name) {
			track('Page Viewed', { ...pageTrackObj, ...adParams });
		}

		// Analytics tracking events on links
		// Old events
		document.querySelectorAll('[data-old-tracking-action]').forEach(function(element) {
			var action = element.getAttribute('data-old-tracking-action');
			var properties = element.getAttribute('data-old-tracking-properties');
			var obj = properties ? JSON.parse(properties) : {};
			var useOldEventMetaData = true;

			trackLink(element, action, obj, useOldEventMetaData);
		});

		// New Mixpanel events
		document.querySelectorAll('[data-tracking-action]').forEach(function(element) {
			var action = element.getAttribute('data-tracking-action');
			var adjustEventName = action.replace(/\s-\s|\s/g, '-').toLowerCase();
			var properties = element.getAttribute('data-tracking-properties');
			var obj = properties ? JSON.parse(properties) : {};

			if (adjustEvents[adjustEventName]) {
				element.addEventListener('click', function() {
					window.Adjust.trackEvent({ eventToken: adjustEvents[adjustEventName] });
				});
			}

			trackLink(element, action, obj);
		});

		if (document.getElementById('login-form')) {
			// Check to see if cookies are enabled and if not, show a warning as users can't log in when cookies are disabled
			if (!navigator.cookieEnabled) {
				var cookieWarningElement = document.createElement('p');

				cookieWarningElement.innerHTML = `
					<p role="alert" class="alert alert--warning alert--extra-padding alert--with-icon">
						<img src="${CDN}/img/icons/alert-circle.svg" alt="">
						<span>Looks like you’ve disabled cookies. Please enable cookies before logging into your account. Read our <a href="/privacy" class="f-link" target="_blank">Privacy Policy</a> to learn more.</span>
					</p>
				`;

				document.querySelector('.login__submit-container').insertBefore(cookieWarningElement, document.getElementById('submit-login'));
			}

			var anonymousIdElement = document.createElement('input');

			anonymousIdElement.setAttribute('type', 'hidden');
			anonymousIdElement.setAttribute('name', 'anonymousId');
			anonymousIdElement.value = anonymousId;

			document.getElementById('login-form').appendChild(anonymousIdElement);

			document.getElementById('submit-login').addEventListener('click', function() {
				track('login client button click', { category: 'login' });
			});
		}
	});

	// Main navigation - make opaque as user scrolls
	stickyHeader();

	// Verify email page
	if (document.querySelector('.verify__new-link')) {
		document.querySelector('.verify__new-link').addEventListener('click', function() {
			return fetch(`/proxy/v1/verify`, { method: 'POST' })
				.then(checkFetchForError)
				.then(parseFetchResponse)
				.then(function() {
					document.querySelector('.verify__sent-copy').classList.remove('hide');
				})
				.catch(function() { });
		});
	}

	// Mobile navigation toggle
	var navWrapper = document.querySelector('.main-nav__wrapper');

	if (navWrapper) {
		document.querySelectorAll('.main-nav__toggle').forEach(function(element) {
			element.addEventListener('click', function(e) {
				e.stopPropagation();

				const mobileNavIsOpen = navWrapper.classList.contains('main-nav__wrapper--mobile-open');

				document.querySelector('.main-nav__nav-menu').setAttribute('aria-expanded', !mobileNavIsOpen);

				if (mobileNavIsOpen) {
					navWrapper.removeAttribute('open');
					clearAllBodyScrollLocks();
				} else {
					navWrapper.open = true;
					disableBodyScroll(navWrapper);
				}

				document.querySelector('.main-nav__mobile-overlay').classList.toggle('main-nav__mobile-overlay--hidden');
				navWrapper.classList.toggle('main-nav__wrapper--mobile-open');
			});
		});
	}

	// Content card
	document.querySelectorAll('.content-card__container--link').forEach(function(element) {
		element.addEventListener('click', function() {
			window.location.href = this.getAttribute('data-link');
		});
	});

	document.addEventListener('DOMContentLoaded', () => {
		var teacherSliderElement = document.querySelector('.teacher__slider-redesign');
		var modalitySliderElement = document.querySelector('.modality-shelf__slider');
		var modalityCarouselElement = document.querySelector('.modality-carousel');
		var countTheWaysSliderElement = document.querySelector('.count-the-ways__slider');

		if (teacherSliderElement) {
			makeSlider(teacherSliderElement);
		}

		if (modalitySliderElement) {
			makeSlider(modalitySliderElement);
		}

		if (modalityCarouselElement) {
			makeSlider(modalityCarouselElement, true);
		}

		if (countTheWaysSliderElement) {
			makeSlider(countTheWaysSliderElement);
		}
	});

	var iterableOptinForm = document.getElementById('iterable-optin');

	if (iterableOptinForm) {
		iterableOptinForm.addEventListener('submit', function(e) {
			e.preventDefault();

			var email = iterableOptinForm.querySelector('input[type="email"]').value.trim();

			if (!email) return;

			var options = {
				headers: {
					'Accept': 'application/json',
					'Content-Type': 'application/json'
				},
				method: 'POST',
				body: JSON.stringify({
					email: email
				})
			};

			fetch(`/emailSignup`, options)
				.then(checkFetchForError)
				.then(function() {
					iterableOptinForm.innerHTML = `
						<h3 class="f-medium">✔️ Got it – we'll keep in touch!</h3>
					`;
				})
				.catch(function() { }); // Fail silently - would rather not expose errors on the homepage of all places :)
		});
	}
})();
